import { NOTIFICATION_EVENTS } from "@edm/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, RefreshCw, Send } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { PageHeader } from "@/components/page-header";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { apiFetch } from "@/lib/api";

interface ChannelRow {
  id: string;
  name: string;
  type: string;
  hasEndpoint: boolean;
  hasToken: boolean;
  recipients: string[];
  events: string[];
  enabled: boolean;
}

const TYPE_LABELS: Record<string, string> = {
  wechat_work: "企业微信机器人",
  webhook: "通用 Webhook",
  sms_http: "短信 HTTP 网关",
};

export function SettingsNotificationsPage() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "",
    type: "wechat_work",
    endpoint: "",
    token: "",
    recipients: "",
    events: ["*"] as string[],
  });
  const query = useQuery({
    queryKey: ["notification-channels"],
    queryFn: () => apiFetch<ChannelRow[]>("/api/v1/admin/notification/channels"),
  });

  async function create() {
    try {
      await apiFetch("/api/v1/admin/notification/channels", {
        method: "POST",
        body: JSON.stringify({
          name: form.name,
          type: form.type,
          endpoint: form.endpoint,
          token: form.token || null,
          recipients: form.recipients.split(/[,\s]+/).filter(Boolean),
          events: form.events,
          enabled: true,
        }),
      });
      await queryClient.invalidateQueries({ queryKey: ["notification-channels"] });
      toast.success("通知渠道已创建");
      setOpen(false);
      setForm({
        name: "",
        type: "wechat_work",
        endpoint: "",
        token: "",
        recipients: "",
        events: ["*"],
      });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "创建失败");
    }
  }

  async function toggle(row: ChannelRow) {
    try {
      await apiFetch(`/api/v1/admin/notification/channels/${row.id}`, {
        method: "PUT",
        body: JSON.stringify({
          name: row.name,
          type: row.type,
          endpoint: null,
          token: null,
          recipients: row.recipients,
          events: row.events,
          enabled: !row.enabled,
        }),
      });
      await queryClient.invalidateQueries({ queryKey: ["notification-channels"] });
      toast.success(row.enabled ? "渠道已停用" : "渠道已启用");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "更新失败");
    }
  }

  async function remove(row: ChannelRow) {
    if (!window.confirm(`确认删除通知渠道「${row.name}」？`)) return;
    try {
      await apiFetch(`/api/v1/admin/notification/channels/${row.id}`, { method: "DELETE" });
      await queryClient.invalidateQueries({ queryKey: ["notification-channels"] });
      toast.success("通知渠道已删除");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "删除失败");
    }
  }

  async function test(id: string) {
    try {
      const result = await apiFetch<{ status: string; error?: string }>(
        `/api/v1/admin/notification/channels/${id}/test`,
        { method: "POST" },
      );
      if (result.status === "sent") toast.success("测试通知已发送");
      else toast.error(`发送失败：${result.error ?? "未知错误"}`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "测试失败");
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="通知渠道"
        description="企业微信机器人、通用 Webhook 和短信 HTTP 网关；按事件订阅，配置加密存储。"
        actions={
          <>
            <Button variant="outline" onClick={() => query.refetch()}>
              <RefreshCw className="h-4 w-4" />
              刷新
            </Button>
            <Button onClick={() => setOpen(true)}>
              <Plus className="h-4 w-4" />
              新建渠道
            </Button>
          </>
        }
      />

      <Alert variant="info" title="可扩展设计">
        Webhook 收到 <code>{`{ event, content }`}</code>；企业微信机器人使用文本消息；短信网关收到{" "}
        <code>{`{ to, content }`}</code>。端点与 Token 加密存 D1。
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle>渠道列表</CardTitle>
          <CardDescription>事件：申请待审批、申请结果、交接待确认，或全部。</CardDescription>
        </CardHeader>
        <CardContent>
          {query.isLoading ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>名称</TableHead>
                  <TableHead>类型</TableHead>
                  <TableHead>事件</TableHead>
                  <TableHead>状态</TableHead>
                  <TableHead className="text-right">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(query.data ?? []).map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="font-medium">{row.name}</TableCell>
                    <TableCell>{TYPE_LABELS[row.type] ?? row.type}</TableCell>
                    <TableCell className="max-w-64 text-xs">{row.events.join(", ")}</TableCell>
                    <TableCell>
                      <Badge variant={row.enabled ? "success" : "secondary"}>
                        {row.enabled ? "启用" : "停用"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => test(row.id)}>
                          <Send className="h-3.5 w-3.5" />
                          测试
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => toggle(row)}>
                          {row.enabled ? "停用" : "启用"}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => remove(row)}>
                          删除
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>新建通知渠道</DialogTitle>
            <DialogDescription>
              企业微信机器人填 Webhook URL；短信网关填接收号码与 HTTP 端点。
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label>名称</Label>
              <Input
                value={form.name}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>类型</Label>
              <select
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={form.type}
                onChange={(event) => setForm({ ...form, type: event.target.value })}
              >
                <option value="wechat_work">企业微信机器人</option>
                <option value="webhook">通用 Webhook</option>
                <option value="sms_http">短信 HTTP 网关</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>端点 URL</Label>
              <Input
                value={form.endpoint}
                onChange={(event) => setForm({ ...form, endpoint: event.target.value })}
                placeholder="https://..."
              />
            </div>
            <div className="space-y-2">
              <Label>Token（可选）</Label>
              <Input
                type="password"
                value={form.token}
                onChange={(event) => setForm({ ...form, token: event.target.value })}
              />
            </div>
            {form.type === "sms_http" ? (
              <div className="space-y-2">
                <Label>接收号码（逗号分隔）</Label>
                <Input
                  value={form.recipients}
                  onChange={(event) => setForm({ ...form, recipients: event.target.value })}
                />
              </div>
            ) : null}
            <div className="space-y-2">
              <Label>订阅事件</Label>
              <div className="space-y-1">
                {NOTIFICATION_EVENTS.map((event) => (
                  <label key={event.code} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={form.events.includes(event.code)}
                      onChange={(ev) =>
                        setForm({
                          ...form,
                          events: ev.target.checked
                            ? [...form.events.filter((code) => code !== "*"), event.code]
                            : form.events.filter((code) => code !== event.code),
                        })
                      }
                    />
                    {event.label}
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              取消
            </Button>
            <Button
              onClick={create}
              disabled={!form.name || !form.endpoint || form.events.length === 0}
            >
              创建
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
